<template>
  <div class="claims-returns-menu">
    <header class="claims-returns-menu__header">
      <h1 class="claims-returns-menu__title">Claims &amp; Returns</h1>
      <p class="claims-returns-menu__subtitle">Choose an action below to get started, or browse resources.</p>
    </header>

    <section class="claims-returns-menu__panel">
      <div class="claims-returns-menu__panel-inner">
        <div class="claims-returns-menu__cards">
          <article v-if="IsClaimsSectionEnabled" class="claims-returns-menu__card">
            <div class="claims-returns-menu__card-header">
              <span class="claims-returns-menu__card-icon">
                <img :src="'/Themes/Default/images/ClaimsReturnsMenu/claims.png'" alt="" />
              </span>
              <h3 class="claims-returns-menu__card-title">Claims</h3>
            </div>
            <p class="claims-returns-menu__card-question">Was an error made on your order?</p>
            <p class="claims-returns-menu__card-text">
              Examples: shorted part, wrong part shipped, carrier damage/loss, questioning the amount of
              freight invoiced, or if you received part numbers not on your pack slip.
            </p>
            <div class="claims-returns-menu__card-actions">
              <router-link
                v-if="hasPermission('CanSubmitDiscrepancyClaims')"
                :to="{ name: 'CreateClaim' }"
                class="claims-returns-menu__card-button">
                Start a Claim
              </router-link>
              <router-link
                v-if="hasPermission('CanViewReturns')"
                to="/claimsAndReturns/claims"
                class="claims-returns-menu__card-button claims-returns-menu__card-button--secondary">
                View Existing Claims
              </router-link>
            </div>
            <p
              v-if="IsOrdersInvoicesSectionEnabled && hasPermission('CanSubmitDiscrepancyClaims')"
              class="claims-returns-menu__card-extra">
              Did you know you can also start a claim from the
              <router-link :to="{ name: 'Invoices' }" class="claims-returns-menu__card-extra-link">Invoices</router-link>
              page?
            </p>
          </article>

          <article v-if="IsReturnsSectionEnabled" class="claims-returns-menu__card">
            <div class="claims-returns-menu__card-header">
              <span class="claims-returns-menu__card-icon">
                <img :src="'/Themes/Default/images/ClaimsReturnsMenu/returns.png'" alt="" />
              </span>
              <h3 class="claims-returns-menu__card-title">Returns</h3>
            </div>
            <p class="claims-returns-menu__card-question">
              Do you have parts that are no longer needed?<br />
              Are you trying to clean up your inventory?
            </p>
            <p class="claims-returns-menu__card-text">
              Submit an express return or utilize your surplus (annual) return.
            </p>
            <div class="claims-returns-menu__card-actions">
              <router-link
                v-if="(hasPermission('CanSubmitExpressReturns') || hasPermission('CanSubmitAnnualReturns')) && !IsMcfs"
                :to="{ name: 'CreateReturn' }"
                class="claims-returns-menu__card-button">
                Start a Return
              </router-link>
              <router-link
                v-if="hasPermission('CanViewReturns')"
                to="/claimsAndReturns/returns"
                class="claims-returns-menu__card-button claims-returns-menu__card-button--secondary">
                View Existing Returns
              </router-link>
            </div>
          </article>

          <article v-if="IsEntitlementsSectionEnabled" class="claims-returns-menu__card">
            <div class="claims-returns-menu__card-header">
              <span class="claims-returns-menu__card-icon">
                <img :src="'/Themes/Default/images/ClaimsReturnsMenu/core_credit.png'" alt="" />
              </span>
              <h3 class="claims-returns-menu__card-title">Core Credits</h3>
            </div>
            <p class="claims-returns-menu__card-question">Do you need to return a core?</p>
            <p class="claims-returns-menu__card-text">
              Submit Open Entitlements to our core processing center via a Core Credit Request (CCR).
            </p>
            <div class="claims-returns-menu__card-actions">
              <router-link
                v-if="hasPermission('CanSubmitCCR')"
                :to="{ name: 'Entitlements' }"
                class="claims-returns-menu__card-button">
                Start a CCR
              </router-link>
              <router-link
                v-if="hasPermission('CanViewCCR')"
                :to="{ name: 'OpenCoreCreditRequests' }"
                class="claims-returns-menu__card-button claims-returns-menu__card-button--secondary">
                View Open CCRs
              </router-link>
            </div>
            <p class="claims-returns-menu__card-extra">
              <router-link
                :to="{ name: 'Entitlements', query: { showExpiredOnly: true } }"
                class="claims-returns-menu__card-extra-link">
                Expired Entitlements
              </router-link>
            </p>
          </article>

          <article v-if="IsMcfa" class="claims-returns-menu__card">
            <div class="claims-returns-menu__card-header">
              <span class="claims-returns-menu__card-icon">
                <img :src="'/Themes/Default/images/ClaimsReturnsMenu/tracs.png'" alt="" />
              </span>
              <h3 class="claims-returns-menu__card-title">TRACS Warranty Claims</h3>
            </div>
            <p class="claims-returns-menu__card-question">Has a part failed that was already installed on a truck?</p>
            <p class="claims-returns-menu__card-text">
              Click the button below to file a claim through the TRACS system. Clicking the link will take you
              to the TRACS Warranty system.
            </p>
            <div class="claims-returns-menu__card-actions">
              <a
                :href="TracsWarrantyUrl || null"
                target="_blank"
                rel="noopener noreferrer"
                class="claims-returns-menu__card-button claims-returns-menu__card-button--wide">
                Start a TRACS Warranty Claim
              </a>
            </div>
          </article>
        </div>
      </div>
    </section>

    <div class="claims-returns-menu__info">
      <section v-if="resourceLinks.length" class="claims-returns-menu__info-card">
        <h2 class="claims-returns-menu__info-title">Resources &amp; Documents</h2>
        <ul class="claims-returns-menu__info-list">
          <li v-for="resource in resourceLinks" :key="resource.title" class="claims-returns-menu__info-item">
            <a :href="resource.url" target="_blank" rel="noopener noreferrer" class="claims-returns-menu__resource-link">
              <fa-icon :icon="['far', 'file-pdf']" class="claims-returns-menu__resource-icon" />
              {{ resource.title }}
              <fa-icon icon="download" class="claims-returns-menu__resource-download" />
            </a>
          </li>
        </ul>
      </section>

      <section class="claims-returns-menu__info-card">
        <h2 class="claims-returns-menu__info-title">Contact Us</h2>
        <ul class="claims-returns-menu__info-list">
          <li v-if="contactEmail" class="claims-returns-menu__info-item claims-returns-menu__contact">
            <img :src="'/Themes/Default/images/ClaimsReturnsMenu/email.png'" alt="" class="claims-returns-menu__contact-icon" />
            <span class="sr-only">Email:</span>
            <a :href="`mailto:${contactEmail}`" class="claims-returns-menu__contact-email">{{ contactEmail }}</a>
          </li>
          <li v-if="contactPhone" class="claims-returns-menu__info-item claims-returns-menu__contact">
            <img :src="'/Themes/Default/images/ClaimsReturnsMenu/phone.png'" alt="" class="claims-returns-menu__contact-icon" />
            <span class="sr-only">Phone:</span>
            <a :href="phoneHref" class="claims-returns-menu__contact-phone">{{ contactPhone }}</a>
          </li>
        </ul>
      </section>
    </div>
  </div>
</template>

<script>
import { siteSectionsHelper } from "@/components/mixins/siteSectionsHelper"
import { ClaimsService } from "@/services"
import { mapGetters } from "vuex"

export default {
  mixins: [siteSectionsHelper],
  data: function () {
    return {
      ClaimsPolicyLink: "",
      ClaimsFilingInstructions: "",
      RemanPolicyLink: "",
      CoreAcceptanceGroupsLink: "",
      TracsWarrantyUrl: "",
      contactEmail: "",
      contactPhone: "",
    }
  },
  computed: {
    ...mapGetters(["hasPermission", "IsMcfs", "IsMcfa"]),
    phoneHref: function () {
      return "tel:" + this.contactPhone.replace(/[^\d+]/g, "")
    },
    resourceLinks: function () {
      const canViewClaimsDocs = (this.IsClaimsSectionEnabled || this.IsReturnsSectionEnabled) && this.hasPermission("CanViewReturns");
      return [
        { title: "Claims Filing Instructions", url: this.ClaimsFilingInstructions, visible: canViewClaimsDocs },
        { title: "Claims and Returns Policy", url: this.ClaimsPolicyLink, visible: canViewClaimsDocs },
        { title: "Reman Policy Document", url: this.RemanPolicyLink, visible: this.IsEntitlementsSectionEnabled },
        { title: "Core Acceptance Groups (CAG)", url: this.CoreAcceptanceGroupsLink, visible: this.IsEntitlementsSectionEnabled },
      ].filter(resource => resource.visible && resource.url);
    }
  },
  mounted: function () {
    ClaimsService.GetClaimsReturnsMenuInfo().then(response => {
      console.log(response);
      const info = response.data || {}
      this.ClaimsPolicyLink = info.ClaimsPolicyUrl || ""
      this.ClaimsFilingInstructions = info.ClaimsFilingInstructionsUrl || ""
      this.RemanPolicyLink = info.RemanPolicyUrl || ""
      this.CoreAcceptanceGroupsLink = info.CoreAcceptanceGroupsUrl || ""
      this.TracsWarrantyUrl = info.TracsWarrantyUrl || ""
      this.contactEmail = info.ContactEmail || ""
      this.contactPhone = info.ContactPhone || ""
    })
  }
}
</script>

<style lang="scss">
$claims-ink: #2a3a4a;
$claims-muted: #6b7785;
$claims-border: #d5dbe2;
$claims-panel-bg: #f0f0f0;
$claims-blue-hover: #005f7f;
$claims-gold: #f8bc16;
$claims-gold-hover: #e0a800;
$claims-gold-light: #fbe08f;
$claims-gold-light-hover: #f8d264;
$claims-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
$claims-content-max-width: 1296px;

// Mirrors the site's .container widths without depending on Bootstrap's class
@mixin claims-page-width {
  width: 100%;
  margin-right: auto;
  margin-left: auto;
  padding-right: 15px;
  padding-left: 15px;

  @media (min-width: $screen-md-min) {
    max-width: 960px;
  }

  @media (min-width: $screen-lg-min) {
    max-width: 85%;
    padding-right: 0;
    padding-left: 0;
  }
}

.claims-returns-menu {
  padding-bottom: 2rem;
  color: $claims-ink;
}

.claims-returns-menu__header {
  margin-bottom: 1rem;

  @include claims-page-width;
}

.claims-returns-menu__title {
  margin: 0 0 0.25rem;
  font-size: 2rem;
  font-weight: 700;
}

.claims-returns-menu__subtitle {
  margin: 0;
  color: $claims-muted;
  font-size: 1rem;
}

.claims-returns-menu__panel {
  margin-bottom: 1.75rem;
  padding: 1.75rem 0 0.75rem;
  background: $claims-panel-bg;
}

.claims-returns-menu__panel-inner {
  @include claims-page-width;
}

.claims-returns-menu__cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 1.25rem;
  max-width: $claims-content-max-width;
  margin: 0 auto;
}

/* Subgrid lines up header, text, and buttons across cards in the same row */
.claims-returns-menu__card {
  display: grid;
  grid-row: span 5;
  grid-template-rows: subgrid;
  row-gap: 0;
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  border: 1px solid $claims-border;
  border-radius: 4px;
  background: #fff;
  box-shadow: $claims-shadow;
  text-align: center;
}

.claims-returns-menu__card-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 1.1rem 0.85rem;
  border-radius: 3px 3px 0 0;
  background: $blue;
}

.claims-returns-menu__card-icon {
  width: 3.5rem;
  height: 3.5rem;
  margin-bottom: 0.6rem;
  overflow: hidden;
  border-radius: 50%;
  background: #fff;
}

.claims-returns-menu__card-icon img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.claims-returns-menu__card-title {
  margin: 0;
  color: #fff;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.claims-returns-menu__card-question {
  margin: 0;
  padding: 1rem 1.4rem 0.3rem;
  font-size: 1.05rem;
  font-weight: 700;
}

.claims-returns-menu__card-text {
  margin: 0;
  padding: 0 1.4rem 1rem;
  min-height: calc(2 * 1.4em + 1rem);
  color: $claims-muted;
  font-size: 0.875rem;
  line-height: 1.4;
  text-wrap: balance;
}

.claims-returns-menu__card-actions {
  display: flex;
  justify-content: center;
  gap: 0.6rem;
  padding: 0 1.4rem;
}

.claims-returns-menu__card-button {
  display: flex;
  flex: 0 1 45%;
  align-items: center;
  justify-content: center;
  padding: 0.8rem 0.85rem;
  border-radius: 3px;
  background: $claims-gold;
  color: $blue;
  font-size: 0.9rem;
  font-weight: 700;
  transition: background-color 0.15s ease;
}

.claims-returns-menu__card-button:hover,
.claims-returns-menu__card-button:focus {
  background: $claims-gold-hover;
  color: $claims-blue-hover;
  text-decoration: none;
}

.claims-returns-menu__card-button:focus-visible {
  outline: 2px solid $blue;
  outline-offset: 2px;
}

.claims-returns-menu__card-button--secondary {
  background: $claims-gold-light;
}

.claims-returns-menu__card-button--secondary:hover,
.claims-returns-menu__card-button--secondary:focus {
  background: $claims-gold-light-hover;
}

.claims-returns-menu__card-button--wide {
  flex-basis: 80%;
}

.claims-returns-menu__card-extra {
  margin: 0;
  padding: 0.7rem 1.4rem 0;
  color: $claims-muted;
  font-size: 0.85rem;
}

.claims-returns-menu__card-extra-link {
  color: $claims-ink;
  font-weight: 700;
  text-decoration: underline;
}

.claims-returns-menu__card-extra-link:hover {
  color: $blue;
}

.claims-returns-menu__info {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
  gap: 1.25rem;

  @include claims-page-width;

  @media (min-width: $screen-lg-min) {
    max-width: min(85%, $claims-content-max-width);
  }
}

.claims-returns-menu__info-card {
  padding: 1.15rem 1.15rem 0.3rem;
  border: 1px solid $claims-border;
  border-radius: 4px;
  background: #fff;
  box-shadow: $claims-shadow;
}

.claims-returns-menu__info-title {
  margin: 0 0 0.6rem;
  font-size: 1.15rem;
  font-weight: 700;
}

.claims-returns-menu__info-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.claims-returns-menu__info-item:not(:first-child) {
  border-top: 1px solid $claims-border;
}

.claims-returns-menu__resource-link {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.85rem 0;
  color: $blue;
  font-size: 0.95rem;
  font-weight: 600;
}

.claims-returns-menu__resource-link:hover {
  color: $claims-blue-hover;
  text-decoration: none;
}

.claims-returns-menu__resource-icon {
  color: #c0392b;
  font-size: 1.4rem;
}

.claims-returns-menu__resource-download {
  margin-left: auto;
  color: #9aa5b1;
}

.claims-returns-menu__contact {
  display: flex;
  align-items: center;
  gap: 1.1rem;
  padding: 1.05rem 0.25rem;
  font-size: 1rem;
}

.claims-returns-menu__contact-icon {
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.35rem;
  object-fit: contain;
}

.claims-returns-menu__contact-email {
  min-width: 0;
  overflow-wrap: anywhere;
  color: $blue;
  font-weight: 600;
  text-decoration: underline;
}

.claims-returns-menu__contact-phone {
  color: $claims-ink;
  font-weight: 700;
}

.claims-returns-menu__contact-email:hover,
.claims-returns-menu__contact-phone:hover {
  color: $claims-blue-hover;
}

@media (max-width: $screen-sm-max) {
  .claims-returns-menu__cards,
  .claims-returns-menu__info {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
